// Commons imports.
import { SERVLET_COMMAND, ServletCmds } from '../../NonReact/constants/ServletCommands'
import { SERVLET_ACTION, ServletActions } from '../../NonReact/constants/React/ServletActions/ServletActions'
import { APIElements, getBaseServletPath } from '../../NonReact/constants/ApiConstants'
import { requestGetJSON } from '../../NonReact/common/API/REST/RESTAPIClient'
import { mapRequestResults } from '../../NonReact/common/API/REST/RESTClient'
import { requestMultiPart } from '../Common/API/MultiPartClient'
import { RESTAPIError } from '../../NonReact/common/API/REST/RESTAPIError'
import { JSONResponse } from '../../NonReact/common/Types/Types'
import { RequestResults } from '../../NonReact/common/Types/MainMenu/ReportListTypes'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import { URLConstructor } from '../../NonReact/utils/URLBuilder'
// Sub imports.
import { CSVResultData } from './Types'

const RES_TYPE_PARAM: string = 'ResType'
const RES_TYPE_JSON: string = 'JSON'
const SLASH: string = '/'

/**
 * Reduces a servlet path to the branding segment {@link getBaseServletPath} expects.
 *
 * Callers disagree on what they pass. The routed users list passes a bare branding segment
 * (for example {@code Akixi2019}), while the legacy admin users page passes {@code sSERVLETPATH},
 * the full context path built by {@code <c:url>} (for example {@code /CCS/App/Akixi2019}). Handing
 * the latter straight to {@link getBaseServletPath} prepended {@code /CCS/App} a second time and
 * produced {@code /CCS/App//CCS/App/Akixi2019}. CCS does not recognise that as a branding style, so
 * it forwards to its 404 page without setting a status - an HTTP 200 HTML body that the upload then
 * failed to parse as JSON (APD-13746).
 *
 * Taking the last non-empty segment accepts both forms.
 *
 * @param servletPath Branding segment or full servlet path from the host page.
 * @returns The branding segment, defaulting to {@link APIElements.AKIXI_2019} when none is present.
 */
const toBrandingSegment = (servletPath: string): string =>
    servletPath.split(SLASH).filter(Boolean).pop() ?? APIElements.AKIXI_2019

/**
 * Builds the legacy servlet JSON URL for bulk-add-users operations.
 *
 * @param servletMapping Branding servlet segment or full servlet path (see {@link toBrandingSegment}).
 * @param action Servlet {@code Action} query value.
 * @returns URL constructor including {@code ServletCmd}, {@code Action}, and {@code ResType=JSON}.
 */
const bulkAddUsersServletURL = (servletMapping: string, action: ServletActions): URLConstructor =>
    getBaseServletPath(toBrandingSegment(servletMapping)).appendQueryParams({
        [SERVLET_COMMAND]: ServletCmds.BULK_ADD_USERS,
        [SERVLET_ACTION]: action,
        [RES_TYPE_PARAM]: RES_TYPE_JSON
    })

/**
 * Uploads file in to temperary file location.
 *
 * @param servletPath Branding servlet segment passed from the host page (same as legacy {@code sSERVLETPATH} basename).
 * @param file The CSV file to upload.
 */
export const uploadCSVFile = async (servletPath: string, file: File): Promise<CSVResultData> => {
    const json: Nullable<JSONResponse<RequestResults<CSVResultData>>> = await requestMultiPart<
        JSONResponse<RequestResults<CSVResultData>>
    >(bulkAddUsersServletURL(servletPath, ServletActions.CSV_FILE_UPLOAD), { 'files[]': file })

    const result: Definable<CSVResultData> = mapRequestResults<CSVResultData>(json ?? null, true)

    if (IsVarEmpty(result)) throw new RESTAPIError(0, null, 'No result data', null)

    return result
}

/**
 * Confirms the file uploaded.
 *
 * @param servletPath Branding servlet segment passed from the host page.
 * @param isErrorIgnored Flag defining if the errors should be ignored and still be passed and dealt with.
 */
export const confirmCSVFile = (servletPath: string, isErrorIgnored: boolean): Promise<Definable<CSVResultData>> =>
    requestGetJSON<RequestResults<CSVResultData>>(
        bulkAddUsersServletURL(servletPath, ServletActions.CONFIRM_FILE)
    ).then(
        (json: JSONResponse<RequestResults<CSVResultData>>): Definable<CSVResultData> =>
            mapRequestResults<CSVResultData>(json, isErrorIgnored)
    )

/**
 * Cancel file upload.
 *
 * @param servletPath Branding servlet segment passed from the host page.
 */
export const cancelCSVFile = (servletPath: string): Promise<Definable<CSVResultData>> =>
    requestGetJSON<RequestResults<CSVResultData>>(bulkAddUsersServletURL(servletPath, ServletActions.CANCEL_FILE)).then(
        (json: JSONResponse<RequestResults<CSVResultData>>): Definable<CSVResultData> =>
            mapRequestResults<CSVResultData>(json, false)
    )
